import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface LayoutState {
  navigationAppearance: 'default' | 'dense';
  navigationOpened: boolean;
  isScreenSmall: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class LayoutStateService {
  private readonly _layoutState = new BehaviorSubject<LayoutState>({
    navigationAppearance: 'dense',
    navigationOpened: true,
    isScreenSmall: false
  });

  /**
   * Observable that emits whenever layout state changes
   */
  readonly layoutState$: Observable<LayoutState> = this._layoutState.asObservable();

  /**
   * Get the current layout state
   */
  get currentState(): LayoutState {
    return this._layoutState.value;
  }

  /**
   * Update navigation appearance (default or dense)
   */
  updateNavigationAppearance(appearance: 'default' | 'dense'): void {
    this._layoutState.next({
      ...this._layoutState.value,
      navigationAppearance: appearance
    });
  }

  /**
   * Update navigation opened state
   */
  updateNavigationOpened(opened: boolean): void {
    this._layoutState.next({
      ...this._layoutState.value,
      navigationOpened: opened
    });
  }

  /**
   * Update screen size state
   */
  updateScreenSize(isSmall: boolean): void {
    this._layoutState.next({
      ...this._layoutState.value,
      isScreenSmall: isSmall
    });
  }

  /**
   * Update all layout state at once
   */
  updateLayoutState(state: Partial<LayoutState>): void {
    this._layoutState.next({
      ...this._layoutState.value,
      ...state
    });
  }

  /**
   * Calculate the navigation width based on current state
   */
  getNavigationWidth(): number {
    const state = this._layoutState.value;
    
    // If screen is small or navigation is closed, width is 0
    if (state.isScreenSmall || !state.navigationOpened) {
      return 0;
    }
    
    // Default appearance: wider navigation (280px)
    // Dense appearance: compact navigation (80px)
    return state.navigationAppearance === 'default' ? 280 : 80;
  }
}